<script setup lang="ts">
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from '~/components/ui/dialog'
import { Button } from '~/components/ui/button'
import { LucideLoaderCircle } from 'lucide-vue-next'
import { FeatureRateAlertForm } from '~/components/feature/rate-alert'
import type { IFeatureRateAlertFormProps } from './types'

const { t } = useI18n()

const props = defineProps<
  IFeatureRateAlertFormProps & {
    open: boolean
    title: string
  }
>()

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
  (e: 'success' | 'cancel'): void
}>()

const isLoading = ref(false)
const formRef = useTemplateRef('form')

const formProps = computed<IFeatureRateAlertFormProps>(() => {
  const { open: _open, title: _title, ...rest } = props
  return rest
})

const handleSuccess = () => {
  emit('update:open', false)
  emit('success')
}

const submitLabel = computed(() =>
  isLoading.value
    ? t('common.loading')
    : props.alert
      ? t('rate_alert.update_alert')
      : t('rate_alert.create_alert')
)

const handleCancel = () => {
  formRef.value?.reset()
  emit('update:open', false)
  emit('cancel')
}
</script>

<template>
  <Dialog :open="props.open" @update:open="emit('update:open', $event)">
    <DialogContent
      class="flex h-full max-h-screen w-full flex-col gap-0 rounded-none p-0 sm:h-auto sm:max-h-[90vh] sm:max-w-2xl sm:rounded-lg"
    >
      <div class="border-b px-6 py-4">
        <DialogTitle class="text-lg font-semibold">{{ props.title }}</DialogTitle>
        <DialogDescription class="sr-only">{{ props.title }}</DialogDescription>
      </div>
      <div class="flex-1 overflow-y-auto">
        <FeatureRateAlertForm
          ref="form"
          v-bind="formProps"
          @success="handleSuccess"
          @update:loading="isLoading = $event"
        />
      </div>
      <DialogFooter class="gap-3 border-t bg-white p-4">
        <Button variant="gray" class="w-full sm:w-auto" @click="handleCancel">
          {{ $t('common.cancel') }}
        </Button>
        <Button type="submit" form="rate-alert-form" :disabled="isLoading" class="w-full sm:w-auto">
          <LucideLoaderCircle v-if="isLoading" class="mr-2 h-4 w-4 animate-spin" />
          {{ submitLabel }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
